import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import LocationMap from './components/LocationMap';
import FAQSection from './components/FAQSection';
import TrustSignals from './components/TrustSignals';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ContactUs = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const contactDetails = {
    phone: '+254 721 320 787',
    whatsapp: '+254721320787',
    email: 'info@peterpartnerslaw.co.ke'
  };

  const handleEmergencyContact = () => {
    const message = encodeURIComponent(currentLanguage === 'en' ?'URGENT: I need immediate legal assistance regarding an emergency matter.' :'DHARURA: Nahitaji msaada wa haraka wa kisheria kuhusu jambo la dharura.');
    window.open(`https://wa.me/${contactDetails.whatsapp}?text=${message}`, '_blank');
  };

  const handleQuickCall = () => {
    window.open(`tel:${contactDetails.phone}`, '_self');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>
          {currentLanguage === 'en' ?'Contact Us - Peter & Partners Advocates LLP | Legal Consultation Kenya' :'Wasiliana Nasi - Peter & Partners Advocates LLP | Mazungumzo ya Kisheria Kenya'
          }
        </title>
        <meta 
          name="description" 
          content={currentLanguage === 'en' ?'Contact Peter & Partners Advocates LLP for professional legal services in Kenya. Free consultation, WhatsApp support, and expert legal advice. Call +254 721 320 787.' :'Wasiliana na Peter & Partners Advocates LLP kwa huduma za kisheria za kitaaluma Kenya. Mazungumzo bure, msaada wa WhatsApp, na ushauri wa kisheria wa kitaaluma. Piga +254 721 320 787.'
          }
        />
        <meta name="keywords" content="contact lawyer Kenya, legal consultation Nairobi, Peter Partners contact, law firm Kenya contact" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-surface/20 to-primary/10 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              {currentLanguage === 'en' ? 'Get in Touch' : 'Wasiliana Nasi'}
            </h1>
            <p className="text-lg md:text-xl text-text-secondary mb-8 leading-relaxed">
              {currentLanguage === 'en' ?'Ready to discuss your legal matter? We\'re here to provide expert guidance and support. Contact us today for a free consultation.'
                : 'Uko tayari kujadili jambo lako la kisheria? Tuko hapa kutoa mwongozo wa kitaaluma na msaada. Wasiliana nasi leo kwa mazungumzo bure.'
              }
            </p>
            
            {/* Quick Contact Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                variant="primary"
                onClick={handleQuickCall}
                iconName="Phone"
                iconPosition="left"
                size="lg"
                className="min-w-48"
              >
                {currentLanguage === 'en' ? 'Call Now' : 'Piga Simu Sasa'}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleEmergencyContact}
                iconName="MessageCircle"
                iconPosition="left"
                size="lg"
                className="min-w-48"
              >
                WhatsApp
              </Button>
            </div>

            {/* Contact Info Display */}
            <div className="mt-8 flex flex-col sm:flex-row gap-6 justify-center items-center text-text-secondary">
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={16} />
                <span>{contactDetails.phone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={16} />
                <span>{contactDetails.email}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span>
                  {currentLanguage === 'en' ? '24hr Response' : 'Majibu ya Masaa 24'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Form - Takes 2 columns on large screens */}
            <div className="lg:col-span-2">
              <ContactForm currentLanguage={currentLanguage} />
            </div>
            
            {/* Contact Information - Takes 1 column on large screens */}
            <div className="lg:col-span-1">
              <ContactInfo currentLanguage={currentLanguage} />
            </div>
          </div>
        </div>
      </section>

      {/* Location Map Section */}
      <section className="py-16 lg:py-24 bg-surface/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <LocationMap currentLanguage={currentLanguage} />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <FAQSection currentLanguage={currentLanguage} />
        </div>
      </section>

      {/* Trust Signals Section */}
      <section className="py-16 lg:py-24 bg-surface/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <TrustSignals currentLanguage={currentLanguage} />
        </div>
      </section>

      {/* Emergency Contact Banner */}
      <section className="py-12 bg-red-50 border-t border-red-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Icon name="AlertTriangle" size={24} color="#EF4444" />
            <h3 className="text-lg font-semibold text-red-900">
              {currentLanguage === 'en' ? 'Emergency Legal Assistance' : 'Msaada wa Dharura wa Kisheria'}
            </h3>
          </div>
          
          <p className="text-red-800 mb-6">
            {currentLanguage === 'en' ?'If you have an urgent legal matter that requires immediate attention, contact us right away.' :'Ikiwa una jambo la haraka la kisheria linaloitaji umakini wa haraka, wasiliana nasi mara moja.'
            }
          </p>
          
          <Button
            variant="danger"
            onClick={handleEmergencyContact}
            iconName="Phone"
            iconPosition="left"
            size="lg"
          >
            {currentLanguage === 'en' ? 'Emergency Contact' : 'Mawasiliano ya Dharura'}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-text-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <Icon name="Scale" size={24} color="white" strokeWidth={2.5} />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-lg">Peter Partners</h4>
                  <p className="text-sm opacity-80">
                    {currentLanguage === 'en' ? 'Law Firm' : 'Kampuni ya Sheria'}
                  </p>
                </div>
              </div>
              <p className="text-sm opacity-80 leading-relaxed">
                {currentLanguage === 'en' ?'Professional legal services with integrity, expertise, and dedication to our clients.' :'Huduma za kisheria za kitaaluma zenye uongozi, utaalamu, na kujitolea kwa wateja wetu.'
                }
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">
                {currentLanguage === 'en' ? 'Quick Links' : 'Viungo vya Haraka'}
              </h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/homepage" className="opacity-80 hover:opacity-100 transition-opacity">
                  {currentLanguage === 'en' ? 'Home' : 'Nyumbani'}
                </a></li>
                <li><a href="/about-us" className="opacity-80 hover:opacity-100 transition-opacity">
                  {currentLanguage === 'en' ? 'About Us' : 'Kuhusu Sisi'}
                </a></li>
                <li><a href="/practice-areas-overview" className="opacity-80 hover:opacity-100 transition-opacity">
                  {currentLanguage === 'en' ? 'Practice Areas' : 'Maeneo ya Utendaji'}
                </a></li>
                <li><a href="/team-profiles" className="opacity-80 hover:opacity-100 transition-opacity">
                  {currentLanguage === 'en' ? 'Our Team' : 'Timu Yetu'}
                </a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold mb-4">
                {currentLanguage === 'en' ? 'Contact Information' : 'Taarifa za Mawasiliano'}
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-2">
                  <Icon name="Phone" size={16} />
                  <span>{contactDetails.phone}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Mail" size={16} />
                  <span>{contactDetails.email}</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="MapPin" size={16} className="mt-0.5" />
                  <span>Westlands Business Centre<br />2nd Floor, Westlands Road<br />Nairobi, Kenya</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 mt-8 pt-8 text-center">
            <p className="text-sm opacity-80">
              © {new Date().getFullYear()} Peter & Partners Advocates LLP. {currentLanguage === 'en' ? 'All rights reserved.' : 'Haki zote zimehifadhiwa.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;